import React from 'react'
import { Card, Button, Tag, Alert, Icon, Spinner, Title, Text } from '@nimbus-ds/components'
import {
  CogIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ExternalLinkIcon,
  StatsIcon,
} from '@nimbus-ds/icons'

import { useGA4Config, useGA4ConfigModal } from '@/hooks/useGA4Config'
import { GA4ConfigurationModal } from './GA4ConfigurationModal'

interface GA4StatusCardProps {
  storeId: string
}

export const GA4StatusCard: React.FC<GA4StatusCardProps> = ({ storeId }) => {
  const {
    config,
    isConfigured,
    isLoading,
    error,
    funnelData,
    isLoadingAnalytics,
    analyticsError,
    refreshAll
  } = useGA4Config({ storeId })

  const { isOpen, openModal, closeModal } = useGA4ConfigModal()

  const handleConfigUpdated = () => {
    // Close modal first
    closeModal()
    // Then refresh data with a slight delay to ensure backend is updated
    setTimeout(() => {
      refreshAll()
    }, 500)
  }

  if (isLoading) {
    return (
      <Card>
        <Card.Header>
          <div className="flex items-center gap-2">
            <Spinner size="small" />
            <Title as="h3">Google Analytics 4</Title>
          </div>
        </Card.Header>
        <Card.Body>
          <div className="space-y-2">
            <div className="h-4 w-3/4 animate-pulse rounded bg-muted" />
            <div className="h-4 w-1/2 animate-pulse rounded bg-muted" />
          </div>
        </Card.Body>
      </Card>
    )
  }

  return (
    <>
      <Card>
        <Card.Header>
          <div className="flex flex-row items-center justify-between gap-2">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <Icon source={<StatsIcon />} />
                <Title as="h3">Google Analytics 4</Title>
                {isConfigured ? (
                  <Tag appearance="success">
                    <Icon source={<CheckCircleIcon />} color="currentColor" />
                    Configurado
                  </Tag>
                ) : (
                  <Tag appearance="warning">
                    <Icon source={<ExclamationTriangleIcon />} color="currentColor" />
                    No configurado
                  </Tag>
                )}
              </div>
              <Text>
                {isConfigured
                  ? 'Mostrando datos reales de funnel desde GA4'
                  : 'Conecta tu propiedad GA4 para datos reales de analítica'
                }
              </Text>
            </div>
            <div className="shrink-0">
              <Button appearance="neutral" onClick={openModal}>
                <Icon source={<CogIcon />} color="currentColor" />
                {isConfigured ? 'Configurar' : 'Conectar GA4'}
              </Button>
            </div>
          </div>
        </Card.Header>

        <Card.Body>
          {error && (
            <div className="mb-4">
              <Alert appearance="danger">{error}</Alert>
            </div>
          )}

          {isConfigured ? (
            <div className="space-y-4">
              {/* GA4 Analytics Summary */}
              {isLoadingAnalytics ? (
                <div className="flex items-center gap-2">
                  <Spinner size="small" />
                  <Text>Cargando datos de analítica...</Text>
                </div>
              ) : analyticsError ? (
                <Alert appearance="danger">{analyticsError}</Alert>
              ) : funnelData && !funnelData.not_configured ? (
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <Text color="neutral-textHigh">Vistas de Producto</Text>
                    <Text fontSize="highlight" fontWeight="bold" color="neutral-textHigh">
                      {funnelData.steps?.view_item?.toLocaleString() || '0'}
                    </Text>
                  </div>
                  <div className="space-y-1">
                    <Text color="neutral-textHigh">Conversiones</Text>
                    <Text fontSize="highlight" fontWeight="bold" color="neutral-textHigh">
                      {funnelData.steps?.purchase?.toLocaleString() || '0'}
                    </Text>
                  </div>
                </div>
              ) : (
                <div>
                  <Text>Configuración completada.</Text>
                  <Text>Los datos de analítica aparecerán aquí una vez que haya tráfico.</Text>
                </div>
              )}

              {/* Configuration Details */}
              <div className="pt-2 border-t">
                <div className="flex items-center justify-between">
                  <Text as="span">Propiedad GA4:</Text>
                  <Text as="span" color="neutral-textHigh">{config?.property_id}</Text>
                </div>
                {funnelData?.property_name && (
                  <div className="flex items-center justify-between mt-1">
                    <Text as="span">Nombre:</Text>
                    <Text as="span" color="neutral-textHigh">{funnelData.property_name}</Text>
                  </div>
                )}
                {funnelData?.last_updated && (
                  <div className="flex items-center justify-between mt-1">
                    <Text as="span">Última actualización:</Text>
                    <Text as="span" color="neutral-textHigh">
                      {new Date(funnelData.last_updated).toLocaleDateString()}
                    </Text>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <Text>Sin GA4 configurado, se muestran estimaciones básicas.</Text>
                <Text>Conecta tu propiedad GA4 para obtener:</Text>
              </div>

              <ul className="space-y-2">
                <li className="flex items-center gap-2">
                  <Icon source={<CheckCircleIcon />} color="success-textLow" />
                  <Text>Datos reales de funnel de conversión</Text>
                </li>
                <li className="flex items-center gap-2">
                  <Icon source={<CheckCircleIcon />} color="success-textLow" />
                  <Text>Métricas de e-commerce precisas</Text>
                </li>
                <li className="flex items-center gap-2">
                  <Icon source={<CheckCircleIcon />} color="success-textLow" />
                  <Text>Análisis de comportamiento de usuario</Text>
                </li>
              </ul>

              <div className="pt-2 border-t">
                <a
                  href="https://developers.google.com/analytics/devguides/reporting/data/v1/quickstart-client-libraries"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1"
                >
                  <Text as="span" color="primary-textLow">Guía de configuración</Text>
                  <Icon source={<ExternalLinkIcon />} color="primary-textLow" />
                </a>
              </div>
            </div>
          )}
        </Card.Body>
      </Card>

      <GA4ConfigurationModal
        isOpen={isOpen}
        onClose={closeModal}
        storeId={storeId}
        onSuccess={handleConfigUpdated}
      />
    </>
  )
}
